import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  User,
  Clock,
  Paperclip,
  Check,
  Flag,
  Minus,
  ChevronUp,
  ChevronsUp,
  FileText,
  Image,
  FileVideo,
  FileAudio,
  File,
  Download,
  ExternalLink,
  Repeat,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import TaskComments from '@/components/tasks/TaskComments';
import { addDays, addWeeks, addMonths, addYears } from 'date-fns';

export function ViewTaskSheet({ open, onOpenChange, task, onTaskUpdate, onEditTask }) {
  const { userRole, userProfile } = useAuth();
  const [updatingSubtask, setUpdatingSubtask] = useState(false);
  const [localTask, setLocalTask] = useState(task);
  const [spaceMembers, setSpaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [myCompletion, setMyCompletion] = useState(false);

  const isAdmin = userRole === 'Admin';

  // Update local task when prop changes
  useEffect(() => {
    setLocalTask(task);
  }, [task]);

  // Fetch space members and my completion status when task changes
  useEffect(() => {
    if (task?.project_id) {
      fetchSpaceMembers(task.project_id);
    }
    if (task?.id && userProfile?.id) {
      fetchMyCompletion();
    }
  }, [task?.project_id, task?.id, userProfile?.id]);

  const fetchSpaceMembers = async (projectId) => {
    try {
      setLoadingMembers(true);
      const { data, error } = await supabase
        .from('space_members')
        .select(`
          user_id,
          user:user_profiles!user_id(id, first_name, last_name, avatar_url)
        `)
        .eq('project_id', projectId);

      if (error) throw error;

      // Flatten the data structure
      const members = data.map(m => m.user);
      setSpaceMembers(members);
    } catch (error) {
      console.error('Error fetching space members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchMyCompletion = async () => {
    try {
      console.log('[ViewTaskSheet] Fetching completion for:', {
        taskId: task.id,
        userId: userProfile.id,
        userName: `${userProfile.first_name} ${userProfile.last_name}`
      });

      const { data, error } = await supabase
        .from('task_completions')
        .select('completed')
        .eq('task_id', task.id)
        .eq('user_id', userProfile.id)
        .maybeSingle();

      console.log('[ViewTaskSheet] Completion fetch result:', {
        data,
        error: error?.code,
        completed: data?.completed || false
      });

      if (error) throw error;
      setMyCompletion(data?.completed || false);
    } catch (error) {
      console.error('Error fetching my completion:', error);
    }
  };

  // Helper to calculate the next due date based on recurrence type
  const calculateNextDueDate = (currentDueDate, recurrenceType) => {
    const baseDate = currentDueDate ? new Date(currentDueDate) : new Date();

    switch (recurrenceType) {
      case 'daily':
        return addDays(baseDate, 1);
      case 'weekly':
        return addWeeks(baseDate, 1);
      case 'biweekly':
        return addWeeks(baseDate, 2);
      case 'monthly':
        return addMonths(baseDate, 1);
      case 'quarterly':
        return addMonths(baseDate, 3);
      case 'yearly':
        return addYears(baseDate, 1);
      default:
        return null;
    }
  };

  // Create the next occurrence of a recurring task
  const createNextOccurrence = async (taskToRecur) => {
    // Only create next occurrence for recurring tasks
    if (!taskToRecur.recurrence_type || taskToRecur.recurrence_type === 'none') {
      return;
    }

    try {
      // Check if a next occurrence already exists (to prevent duplicates)
      const { data: existingNext, error: checkError } = await supabase
        .from('tasks')
        .select('id')
        .eq('parent_task_id', taskToRecur.id)
        .limit(1);

      if (checkError) throw checkError;

      // If there's already a next occurrence, don't create another
      if (existingNext && existingNext.length > 0) {
        console.log('[ViewTaskSheet] Next occurrence already exists for task:', taskToRecur.id);
        return;
      }

      // Calculate the next due date
      const nextDueDate = calculateNextDueDate(taskToRecur.due_date, taskToRecur.recurrence_type);

      // Check recurrence end date
      if (taskToRecur.recurrence_end_date && nextDueDate > new Date(taskToRecur.recurrence_end_date)) {
        console.log('[ViewTaskSheet] Recurrence end date reached for task:', taskToRecur.id);
        toast.info('This was the last occurrence of this recurring task');
        return;
      }

      // Create the next occurrence
      const { data: newTask, error: createError } = await supabase
        .from('tasks')
        .insert({
          name: taskToRecur.name,
          description: taskToRecur.description,
          project_id: taskToRecur.project_id,
          created_by: taskToRecur.created_by,
          assigned_to: taskToRecur.assigned_to,
          due_date: nextDueDate?.toISOString(),
          priority: taskToRecur.priority,
          subtasks: taskToRecur.subtasks?.map(st => ({ ...st, completed: false })), // Reset subtasks
          link_to_workflow: taskToRecur.link_to_workflow,
          recurrence_type: taskToRecur.recurrence_type,
          recurrence_interval: taskToRecur.recurrence_interval,
          recurrence_end_date: taskToRecur.recurrence_end_date,
          parent_task_id: taskToRecur.parent_task_id || taskToRecur.id, // Link to original parent
          recurrence_count: (taskToRecur.recurrence_count || 0) + 1,
        })
        .select()
        .single();

      if (createError) throw createError;

      console.log('[ViewTaskSheet] Created next occurrence:', newTask.id);
      toast.success('Next recurring task created');
    } catch (error) {
      console.error('[ViewTaskSheet] Error creating next occurrence:', error);
    }
  };

  const handleToggleMyCompletion = async () => {
    try {
      const newStatus = !myCompletion;

      console.log('[ViewTaskSheet] Toggling completion:', {
        taskId: task.id,
        userId: userProfile.id,
        userName: `${userProfile.first_name} ${userProfile.last_name}`,
        oldStatus: myCompletion,
        newStatus: newStatus
      });

      // Optimistically update
      setMyCompletion(newStatus);

      const { error } = await supabase
        .from('task_completions')
        .upsert({
          task_id: task.id,
          user_id: userProfile.id,
          completed: newStatus,
          completed_at: newStatus ? new Date().toISOString() : null
        }, {
          onConflict: 'task_id,user_id'
        });

      if (error) throw error;

      console.log('[ViewTaskSheet] Completion toggled successfully:', {
        userId: userProfile.id,
        newStatus
      });

      toast.success(newStatus ? 'Task marked as complete' : 'Task reopened');

      // If marking as complete and it's a recurring task, create the next occurrence
      if (newStatus && localTask.recurrence_type && localTask.recurrence_type !== 'none') {
        await createNextOccurrence(localTask);
      }

      // Refresh task list
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('[ViewTaskSheet] Error toggling completion:', error);
      toast.error('Failed to update task');
      // Revert
      setMyCompletion(!myCompletion);
    }
  };

  if (!localTask) return null;

  const handleSubtaskToggle = async (subtaskId) => {
    try {
      setUpdatingSubtask(true);

      // Toggle the subtask's completed status
      const updatedSubtasks = localTask.subtasks.map((st) =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      );

      // Optimistically update local state immediately
      setLocalTask({
        ...localTask,
        subtasks: updatedSubtasks
      });

      // Update in database
      const { error } = await supabase
        .from('tasks')
        .update({ subtasks: updatedSubtasks })
        .eq('id', localTask.id);

      if (error) throw error;

      // No need to refresh the entire tasks list - subtasks aren't visible in the table
    } catch (error) {
      console.error('Error updating subtask:', error);
      toast.error('Failed to update subtask');
      // Revert optimistic update on error
      setLocalTask(task);
    } finally {
      setUpdatingSubtask(false);
    }
  };

  const handleToggleAssignee = async (memberId) => {
    try {
      const currentAssignees = Array.isArray(localTask.assigned_to)
        ? localTask.assigned_to
        : [];

      let newAssignees;
      if (currentAssignees.includes(memberId)) {
        // Remove member
        newAssignees = currentAssignees.filter(id => id !== memberId);
      } else {
        // Add member
        newAssignees = [...currentAssignees, memberId];
      }

      // Optimistically update local state
      setLocalTask({
        ...localTask,
        assigned_to: newAssignees
      });

      // Update in database
      const { error } = await supabase
        .from('tasks')
        .update({ assigned_to: newAssignees })
        .eq('id', localTask.id);

      if (error) throw error;

      const member = spaceMembers.find(m => m.id === memberId);
      const action = newAssignees.includes(memberId) ? 'assigned to' : 'unassigned from';
      toast.success(`Task ${action} ${member.first_name} ${member.last_name}`);

      // Refresh the tasks list so the table shows the new assignees
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('Error updating task assignment:', error);
      toast.error('Failed to update assignment');
      // Revert optimistic update on error
      setLocalTask(task);
    }
  };

  const handleAssignAll = async () => {
    try {
      const allMemberIds = spaceMembers.map(m => m.id);

      // Optimistically update local state
      setLocalTask({
        ...localTask,
        assigned_to: allMemberIds
      });

      // Update in database
      const { error } = await supabase
        .from('tasks')
        .update({ assigned_to: allMemberIds })
        .eq('id', localTask.id);

      if (error) throw error;

      toast.success('Task assigned to all space members');

      // Refresh the tasks list
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('Error assigning to all:', error);
      toast.error('Failed to assign to all');
      // Revert optimistic update on error
      setLocalTask(task);
    }
  };

  const handleDueDateChange = async (date) => {
    try {
      // Optimistically update local state
      setLocalTask({
        ...localTask,
        due_date: date ? date.toISOString() : null
      });

      // Update in database
      const { error } = await supabase
        .from('tasks')
        .update({ due_date: date ? date.toISOString() : null })
        .eq('id', localTask.id);

      if (error) throw error;

      toast.success(date ? 'Due date set' : 'Due date cleared');

      // Refresh the tasks list
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Failed to update due date');
      // Revert optimistic update on error
      setLocalTask(task);
    }
  };

  // Get assigned members from the assigned_to array
  const assignedIds = Array.isArray(localTask.assigned_to)
    ? localTask.assigned_to
    : [];
  const assignedMembers = spaceMembers.filter(m => assignedIds.includes(m.id));

  const dueDate = localTask.due_date
    ? new Date(localTask.due_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Priority configuration
  const priorityOptions = {
    low: { label: 'Low', icon: Minus, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-200' },
    medium: { label: 'Medium', icon: Flag, color: 'text-blue-500', bg: 'bg-blue-100', border: 'border-blue-200' },
    high: { label: 'High', icon: ChevronUp, color: 'text-orange-500', bg: 'bg-orange-100', border: 'border-orange-200' },
    urgent: { label: 'Urgent', icon: ChevronsUp, color: 'text-red-500', bg: 'bg-red-100', border: 'border-red-200' },
  };

  const currentPriority = localTask.priority ? priorityOptions[localTask.priority] : null;
  const PriorityIcon = currentPriority?.icon;

  // Recurrence configuration
  const recurrenceLabels = {
    none: 'Does not repeat',
    daily: 'Daily',
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };

  const currentRecurrence = localTask.recurrence_type && localTask.recurrence_type !== 'none'
    ? recurrenceLabels[localTask.recurrence_type]
    : null;

  // Helper to get file icon based on type
  const getFileIcon = (fileType) => {
    if (!fileType) return File;
    if (fileType.startsWith('image/')) return Image;
    if (fileType.startsWith('video/')) return FileVideo;
    if (fileType.startsWith('audio/')) return FileAudio;
    if (fileType.includes('pdf') || fileType.includes('document') || fileType.includes('text')) return FileText;
    return File;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-sm font-medium tracking-wider text-muted-foreground">
            TASK
          </SheetTitle>
          <SheetDescription className="sr-only">
            View and manage task details
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {loadingMembers ? (
                  <DropdownMenuItem disabled>Loading members...</DropdownMenuItem>
                ) : spaceMembers.length === 0 ? (
                  <DropdownMenuItem disabled>No team members found</DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={handleAssignAll}
                      className="flex items-center gap-2 font-medium"
                    >
                      <Checkbox
                        checked={assignedIds.length === spaceMembers.length}
                        className="pointer-events-none"
                      />
                      <span>All space members</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {spaceMembers.map((member) => (
                      <DropdownMenuItem
                        key={member.id}
                        onClick={() => handleToggleAssignee(member.id)}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          checked={assignedIds.includes(member.id)}
                          className="pointer-events-none"
                        />
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {member.first_name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span>{member.first_name} {member.last_name}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {assignedMembers.length > 0 && (
              <div className="flex -space-x-2">
                {assignedMembers.slice(0, 3).map((member) => (
                  <Avatar key={member.id} className="h-9 w-9 border-2 border-background">
                    <AvatarImage src={member.avatar_url} />
                    <AvatarFallback className="text-xs">
                      {member.first_name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {assignedMembers.length > 3 && (
                  <div className="h-9 w-9 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
                    +{assignedMembers.length - 3}
                  </div>
                )}
              </div>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={dueDate ? "default" : "outline"}
                  size={dueDate ? "sm" : "icon"}
                  className={dueDate ? "gap-2 h-9" : "h-9 w-9"}
                >
                  <Clock className="h-4 w-4" />
                  {dueDate && <span>{dueDate.split(',')[0]}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={localTask.due_date ? new Date(localTask.due_date) : null}
                  onSelect={handleDueDateChange}
                  initialFocus
                />
                {localTask.due_date && (
                  <div className="p-3 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => handleDueDateChange(null)}
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Priority Badge */}
            {currentPriority && PriorityIcon && (
              <Badge
                variant="outline"
                className={`gap-1 h-9 px-3 ${currentPriority.bg} ${currentPriority.border} ${currentPriority.color}`}
              >
                <PriorityIcon className="h-4 w-4" />
                {currentPriority.label}
              </Badge>
            )}

            {/* Recurrence Badge */}
            {currentRecurrence && (
              <Badge
                variant="outline"
                className="gap-1 h-9 px-3 bg-purple-100 border-purple-200 text-purple-600"
              >
                <Repeat className="h-4 w-4" />
                {currentRecurrence}
              </Badge>
            )}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold">{localTask.name}</h2>
          </div>

          {/* Description */}
          {localTask.description && (
            <div className="space-y-2">
              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: localTask.description }}
              />
            </div>
          )}

          {/* Subtasks */}
          {localTask.subtasks && localTask.subtasks.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-2">
                <h3 className="text-xs font-medium tracking-wider text-muted-foreground">
                  SUBTASKS
                </h3>
                <div className="space-y-2">
                  {localTask.subtasks.map((subtask, index) => (
                    <div key={subtask.id} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-6">
                        {index + 1}
                      </span>
                      <Checkbox
                        checked={subtask.completed}
                        onCheckedChange={() => handleSubtaskToggle(subtask.id)}
                        disabled={updatingSubtask}
                      />
                      <span className={`flex-1 text-sm ${subtask.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {subtask.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Attachments */}
          {localTask.attachments && localTask.attachments.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-medium tracking-wider text-muted-foreground">
                ATTACHMENTS
              </h3>
              <div className="space-y-2">
                {localTask.attachments.map((file, index) => {
                  const FileIcon = getFileIcon(file.type);
                  const isImage = file.type?.startsWith('image/');

                  return (
                    <div
                      key={index}
                      className="group flex items-center gap-3 p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      {/* File Icon or Image Preview */}
                      {isImage ? (
                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-muted">
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                          <FileIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        {file.size && (
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(file.url, '_blank');
                          }}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              // Extract file path from URL (remove base URL)
                              const bucketUrl = 'https://storage.googleapis.com/trainingmatrix/';
                              const filePath = file.url.startsWith(bucketUrl)
                                ? file.url.replace(bucketUrl, '')
                                : file.url;

                              // Get signed download URL with content-disposition
                              const { data, error } = await supabase.functions.invoke('generate-upload-url', {
                                body: {
                                  action: 'download',
                                  filePath: filePath,
                                },
                              });

                              if (error) throw error;

                              // Open signed URL - browser will download due to content-disposition header
                              window.open(data.signedUrl, '_blank');
                            } catch (error) {
                              console.error('Error downloading file:', error);
                              toast.error('Failed to download file');
                            }
                          }}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Workflow Link Info */}
          {localTask.link_to_workflow && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Paperclip className="h-4 w-4" />
                <span>Linked to workflow</span>
              </div>
            </div>
          )}

          {/* Task Metadata */}
          <div className="pt-4 border-t space-y-2 text-sm text-muted-foreground">
            {currentPriority && (
              <div className="flex items-center justify-between">
                <span>Priority</span>
                <div className={`flex items-center gap-1 ${currentPriority.color}`}>
                  {PriorityIcon && <PriorityIcon className="h-4 w-4" />}
                  <span>{currentPriority.label}</span>
                </div>
              </div>
            )}
            {currentRecurrence && (
              <div className="flex items-center justify-between">
                <span>Repeats</span>
                <div className="flex items-center gap-1 text-purple-600">
                  <Repeat className="h-4 w-4" />
                  <span>{currentRecurrence}</span>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span>Created by</span>
              <div className="flex items-center gap-2">
                {localTask.creator && (
                  <>
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={localTask.creator.avatar_url} />
                      <AvatarFallback className="text-xs">
                        {localTask.creator.first_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {localTask.creator.first_name} {localTask.creator.last_name}
                    </span>
                  </>
                )}
              </div>
            </div>
            {assignedMembers.length > 0 && (
              <div className="flex items-center justify-between">
                <span>Assigned to</span>
                <div className="flex items-center gap-1">
                  {assignedMembers.slice(0, 5).map((member) => (
                    <Avatar key={member.id} className="h-5 w-5">
                      <AvatarImage src={member.avatar_url} />
                      <AvatarFallback className="text-xs">
                        {member.first_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {assignedMembers.length > 5 && (
                    <span className="text-xs">+{assignedMembers.length - 5} more</span>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span>Created</span>
              <span>
                {new Date(localTask.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            </div>
            {localTask.updated_at !== localTask.created_at && (
              <div className="flex items-center justify-between">
                <span>Last updated</span>
                <span>
                  {new Date(localTask.updated_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="pt-6 border-t">
            <TaskComments
              taskId={localTask.id}
              taskName={localTask.name}
              projectId={localTask.project_id}
            />
          </div>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="border-t px-6 py-4 mt-auto bg-background">
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4"
            >
              Close
            </Button>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Button
                  variant="outline"
                  className="h-10 px-4"
                  onClick={() => onEditTask(localTask)}
                >
                  Edit
                </Button>
              )}
              <Button
                variant={myCompletion ? 'outline' : 'default'}
                className="h-10 px-6"
                onClick={handleToggleMyCompletion}
              >
                {myCompletion ? 'Reopen' : 'Mark Complete'}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
